import { tm } from '../tm'
import { UrgentProjectBadge } from '../UrgentProjectBadge'
import { YoutubeIframe } from '../YoutubeIframe'
import type { Project } from '@captaincause/types'
import type { CSSProperties } from 'react'

type Field = 'youtube_id' | 'image' | 'image2' | 'image3' | 'image4' | 'image5'

type HeroAssets = { field: Field; src: string; type: 'youtube' | 'image' }

function getAssets(project: Project): HeroAssets[] {
  return (
    [
      { field: 'youtube_id', src: project.youtube_id, type: 'youtube' },
      { field: 'image', src: project.image, type: 'image' },
      { field: 'image2', src: project.image2, type: 'image' },
      { field: 'image3', src: project.image3, type: 'image' },
      { field: 'image4', src: project.image4, type: 'image' },
      { field: 'image5', src: project.image5, type: 'image' },
    ] as HeroAssets[]
  ).filter(data => !!data.src)
}

interface ProjectImagesProps {
  project: Project
  className?: string
  imageClassName?: string
  imageContainerClassName?: string
  style?: CSSProperties
  excludedImages?: Field[]
}

const emptyObject = {}
export function ProjectImages({
  project,
  className = undefined,
  imageClassName = undefined,
  imageContainerClassName = undefined,
  excludedImages = undefined,
  style = emptyObject,
}: ProjectImagesProps) {
  const assets = getAssets(project)

  const filteredAssets = excludedImages
    ? assets.filter(asset => !excludedImages.includes(asset.field))
    : assets

  const mobileHeroAsset = filteredAssets.find(asset => asset.type !== 'youtube')

  if (filteredAssets.length === 0) return null

  const assetsLength = filteredAssets.length

  return (
    <div
      className={tm(
        'grid h-[200px] grid-cols-1 md:col-start-1 md:col-end-3 md:h-[300px]',
        assetsLength === 1 && 'w-full',
        assetsLength === 2 && 'gap-x-2 md:grid-cols-2',
        assetsLength === 3 && 'md:grid-cols-[50%_auto_auto] md:gap-x-2',
        assetsLength >= 4 &&
          'md:grid-cols-[50%_auto_auto] md:grid-rows-2 md:gap-2',
        className,
      )}
      style={style}
    >
      {mobileHeroAsset ? (
        <div className="row-start-1 row-end-3 md:hidden">
          <div
            className={tm(
              'relative mx-auto h-full w-full rounded-md',
              imageContainerClassName,
            )}
          >
            <img
              src={mobileHeroAsset.src}
              alt=""
              className={tm(
                'absolute left-0 top-0 h-full w-full object-cover',
                imageClassName,
              )}
            />
            <UrgentProjectBadge
              className="absolute bottom-2 left-2 md:hidden"
              project={project}
            />
          </div>
        </div>
      ) : null}
      {filteredAssets.map((asset, index) => (
        <div
          key={asset.src}
          className={tm(
            'hidden md:block',
            assetsLength >= 4 && index === 0 && 'row-start-1 row-end-3',
            assetsLength === 4 && index === 1 && 'col-start-2 col-end-4',
          )}
        >
          {asset.type === 'youtube' ? (
            <YoutubeIframe
              id={asset.src}
              className={tm(
                'mt-0 !h-full !w-full rounded-none md:rounded-md',
                imageClassName,
              )}
            />
          ) : (
            <div
              className={tm(
                'relative mx-auto h-full w-full rounded-md',
                imageContainerClassName,
              )}
            >
              <img
                src={asset.src}
                alt=""
                className={tm(
                  'absolute left-0 top-0 h-full w-full object-cover md:rounded-md',
                  imageClassName,
                )}
              />
              {index === 0 ? (
                <UrgentProjectBadge
                  className="absolute bottom-2 left-2 hidden md:block"
                  project={project}
                />
              ) : null}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
